import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { useCreateWorkspaceInvite } from "@/queries/workspace";
import { WorkspaceInviteFormValues } from "@/schemas/workspace-invite";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums/toast";
import { ENDPOINTS } from "@/api/application-api";

interface SubmitProps {
  onSuccess: () => void;
}

const Submit = ({ onSuccess }: SubmitProps) => {
  const createWorkspaceInvite = useCreateWorkspaceInvite();
  const form = useFormContext<WorkspaceInviteFormValues>();
  const emailCount = form.watch("emails")?.length ?? 0;
  const workspaceCount = form.watch("workspace_ids")?.length ?? 0;
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  let invite = "Invite";
  if (emailCount > 1) {
    invite = `${invite} ${emailCount} people`;
  } else if (emailCount > 0) {
    invite = `${invite} ${emailCount} person`;
  }
  if (workspaceCount > 1) {
    invite = `${invite} to ${workspaceCount} workspaces`;
  }

  const handleClick = form.handleSubmit(async (data: WorkspaceInviteFormValues) => {
    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        data.workspace_ids.map(workspaceId =>
          createWorkspaceInvite.mutateAsync({
            workspace_id: workspaceId,
            emails: data.emails,
          }),
        ),
      );

      const succeeded = results.filter(r => r.status === "fulfilled");
      const failed = results.filter(r => r.status === "rejected");

      const totalInvited = succeeded.reduce((sum, r) => {
        const val = r as PromiseFulfilledResult<{ invited: unknown[] }>;
        return sum + (Array.isArray(val.value?.invited) ? val.value.invited.length : 0);
      }, 0);

      if (failed.length === 0) {
        const message =
          totalInvited === 1
            ? "Successfully sent 1 invitation"
            : `Successfully sent ${totalInvited} invitations`;
        displayToast(message, ToastType.success);
      } else if (succeeded.length === 0) {
        const firstError = (failed[0] as PromiseRejectedResult).reason;
        displayErrorToast(firstError);
      } else {
        displayToast(
          `Sent ${totalInvited} invitation${totalInvited > 1 ? "s" : ""}, ${failed.length} workspace${failed.length > 1 ? "s" : ""} failed`,
          ToastType.warning,
        );
      }

      queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <DialogFooter>
      <DialogClose>
        <Button variant="secondary">Cancel</Button>
      </DialogClose>
      <Button
        onClick={handleClick}
        disabled={!form.formState.isValid || emailCount === 0 || isSubmitting}
        isLoading={isSubmitting}
      >
        {isSubmitting ? "Sending..." : invite}
      </Button>
    </DialogFooter>
  );
};

export default Submit;
